import { addQueryParamsToUrl } from '@healthhub/react-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { OutlinedLeftArrowIcon } from '@shared/assets-icon';
import { MY_CHILDREN_PROFILE_DETAILS_PATH, MY_FAMILY_AND_FRIEND_HEALTH_DETAILS_PATH } from '@shared/constants/paths';
import { useLayoutViewContext } from '@shared/context';
import useGlobalBreadcrumbs from '@shared/hooks/useGlobalBreadcrumbs';
import useMemberIdentifier from '@shared/hooks/useMemberIdentifier';
import { useAppSelector } from '@shared/stores/rootTypes';
import { selectedProfileSelector } from '@shared/stores/user/userSelector';

import type { BreadCrumbDetails } from './CustomBreadcrumb.types';
import { Button } from '../Button';

const DEFAULT_NAV_PATH: Record<string, { path: string; title: string }> = {
  CH: {
    path: MY_CHILDREN_PROFILE_DETAILS_PATH,
    title: 'leftNavText.childDetails',
  },
  FH: {
    path: MY_FAMILY_AND_FRIEND_HEALTH_DETAILS_PATH,
    title: 'leftNavText.familyAndFriendsDetails',
  },
};

export interface CustomBreadcrumbProps {
  breadcrumbsDetails?: BreadCrumbDetails[];
  className?: string;
  isIncludedDefaultNav?: boolean;
  isIncludedEservices?: boolean;
}

const CustomBreadcrumb = ({
  breadcrumbsDetails,
  className,
  isIncludedDefaultNav = true,
  isIncludedEservices = true,
}: CustomBreadcrumbProps) => {
  const { isPortalView } = useLayoutViewContext();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { memberIdentifier } = useMemberIdentifier({
    isIncludedCaregivers: true,
  });
  const selectedProfile = useAppSelector(selectedProfileSelector);

  const memoizedBreadcrumbsDetails = useMemo(() => {
    const defaultNav = DEFAULT_NAV_PATH[selectedProfile?.sourceCode ?? ''];

    const newBreadcrumbsDetails = breadcrumbsDetails?.map((breadcrumb) => {
      if (!breadcrumb.isAppendMemberIdentifier) {
        return breadcrumb;
      }

      if (breadcrumb.path) {
        return {
          ...breadcrumb,
          customOnClick: () => {
            navigate(addQueryParamsToUrl(breadcrumb.path ?? '', { mid: memberIdentifier }));
          },
        };
      }

      return breadcrumb;
    });

    if (isIncludedDefaultNav && memberIdentifier && defaultNav) {
      return [
        {
          customOnClick: () => {
            navigate(addQueryParamsToUrl(defaultNav.path, { mid: memberIdentifier }));
          },
          title: t(defaultNav.title),
        },
        ...(newBreadcrumbsDetails ?? []),
      ];
    }

    return newBreadcrumbsDetails;
  }, [breadcrumbsDetails, isIncludedDefaultNav, memberIdentifier, navigate, selectedProfile?.sourceCode, t]);

  const memoizedLayout = useMemo(
    () => ({
      portal: {
        className,
        data: memoizedBreadcrumbsDetails,
        isIncludedEservices,
      },
    }),
    [memoizedBreadcrumbsDetails, isIncludedEservices, className]
  );

  useGlobalBreadcrumbs(memoizedLayout);

  if (isPortalView || !breadcrumbsDetails || breadcrumbsDetails?.length === 0) {
    return null;
  }

  const { customOnClick, isAppendMemberIdentifier, path = '', title } = breadcrumbsDetails[0];

  const onClickBreadcrumb = () => {
    if (customOnClick) {
      customOnClick();
      return;
    }

    if (path) {
      const urlWithViewPath = path;
      navigate(
        isAppendMemberIdentifier ? addQueryParamsToUrl(urlWithViewPath, { mid: memberIdentifier }) : urlWithViewPath
      );
    }
  };

  return (
    <Button className="flex items-center self-start text-sm font-semibold" onClick={onClickBreadcrumb} variant="text">
      <OutlinedLeftArrowIcon className="size-6" />
      {title}
    </Button>
  );
};

export default CustomBreadcrumb;
